import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  InputLabel,
  Grid,
  Typography,
  FormControl,
  MenuItem,
  Avatar,
  TextField,
  Select,
} from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import { Waiting } from "../../components/Waiting";
import OrgLogoForm from "./OrgLogoForm";
import { SketchPicker } from "react-color";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useCustomSnackbar } from "../../components/CustomSnackbar";
import { gql } from "@apollo/client";
import { GET_ORG_PROFILE } from "../../graphql/queries";
import { useQuery, useMutation } from "@apollo/client";
import { copySiteConfig } from "../../siteConfig";
import { useAppContext } from "../../context/AppContext";

export default function OrgDetailsForm(props) {
  const inputLabel = React.useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lsEnqueueSnackbar } = useCustomSnackbar();
  const [displayColorPrimaryPicker, setDisplayColorPrimaryPicker] =
    useState(false);
  const [displayColorSecondaryPicker, setDisplayColorSecondaryPicker] =
    useState(false);
  const [primarycolor, setPrimaryColor] = useState("#0340d1");
  const [secondarycolor, setSecondaryColor] = useState("#ff8c73");
  const [customSubdomain, setCustomSubdomain] = useState("");

  const { store } = useAppContext();

  const { data: getOPData, loading: getOPLoading } = useQuery(GET_ORG_PROFILE, {
    fetchPolicy: "network-only",
    variables: {
      orgId: store.currentOrg.org.id,
    },
    onError: (e) => {
      lsEnqueueSnackbar(e.message, {
        variant: "error",
      });
    },
  });

  let orgProfile = useRef(getOPData?.orgProfile);
  let setOrgProfile = (op) => {
    orgProfile.current = op;
  };

  // Ensures that colors selected are set immediately
  useEffect(() => {
    if (getOPData && getOPData.orgProfile) {
      orgProfile.current = getOPData.orgProfile;
      if (orgProfile.current.brandcolors) {
        setPrimaryColor(orgProfile.current.brandcolors.primarycolor.trim());
        setSecondaryColor(orgProfile.current.brandcolors.secondarycolor.trim());
      }
      if (customSubdomain === "" && orgProfile.current.customsubdomain) {
        setCustomSubdomain(orgProfile.current.customsubdomain.trim());
      }
    }
  }, [getOPData, customSubdomain]);

  // Set up the update user profile LeadStory API
  const UPDATE_ORG_PROFILE = gql`
    mutation UpdateOrgProfile($orgId: GUID!, $op: OrgProfile!) {
      updateOrgProfile(orgId: $orgId, orgProfile: $op)
    }
  `;
  const [updateOrgProfile] = useMutation(UPDATE_ORG_PROFILE);

  // Handles opening, closing and state setting for the two color pickers
  const handleClickPrimaryColor = () => {
    setDisplayColorPrimaryPicker(true);
  };

  const handleClosePrimaryColor = () => {
    setDisplayColorPrimaryPicker(false);
  };

  const handleClickSecondaryColor = () => {
    setDisplayColorSecondaryPicker(true);
  };

  const handleCloseSecondaryColor = () => {
    setDisplayColorSecondaryPicker(false);
  };

  const handlePrimaryColorChange = (color, event) => {
    setPrimaryColor(color.hex);
  };

  const handleSecondaryColorChange = (color, event) => {
    setSecondaryColor(color.hex);
  };

  // Additional styles needed for color picker
  const popover = {
    position: "absolute",
    zIndex: "2",
  };

  const cover = {
    position: "fixed",
    top: "0px",
    right: "0px",
    bottom: "0px",
    left: "0px",
  };

  // Used to mask region string
  const regionLookup = {
    "us-west-2": "Oregon",
    "us-west-1": "California",
    "us-east-2": "Ohio",
    "us-east-1": "Virginia",
    "ca-central-1": "Central Canada",
  };

  const validationSchema = Yup.object({
    name: Yup.string().max(20, t("TwentyOrFewerChars")).required(t("Required")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: orgProfile?.current?.name ? orgProfile.current.name.trim() : "",
      address: orgProfile?.current?.address
        ? orgProfile?.current.address.trim()
        : "",
      city: orgProfile?.current?.city ? orgProfile.current.city.trim() : "",
      state: orgProfile?.current?.state ? orgProfile.current.state.trim() : "",
      postalcode: orgProfile?.current?.postalcode
        ? orgProfile.current.postalcode.trim()
        : "",
      country: orgProfile?.current?.country
        ? orgProfile.current.country.trim()
        : "",
      language: orgProfile?.current?.language
        ? orgProfile.current.language.trim()
        : "",
      timezone: orgProfile?.current?.timezone
        ? orgProfile.current.timezone.trim()
        : "",
      region: orgProfile?.current?.region
        ? regionLookup[orgProfile.current.region.trim()]
        : "",
      primarycolor: primarycolor,
      secondarycolor: secondarycolor,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const vars = {
        variables: {
          orgId: store.currentOrg.org.id,
          op: {
            brandcolors: {
              primarycolor: primarycolor,
              secondarycolor: secondarycolor,
            },
            name: values.name,
            address: values.address,
            city: values.city,
            state: values.state,
            postalcode: values.postalcode,
            country: values.country,
            language: values.language,
            timezone: values.timezone,
          },
        },
      };

      // Update the org profile on the server
      updateOrgProfile(vars)
        .then((res) => {
          if (res.data) {
            lsEnqueueSnackbar(t("OrgUpdateSuccessful"), {
              variant: "success",
            });

            // if this is an org site, update the site configuration.
            if (props.siteConfig.orgId) {
              let newSiteConfig = copySiteConfig(props.siteConfig);
              newSiteConfig.siteName = values.name;
              newSiteConfig.brandcolors = {
                primarycolor: primarycolor,
                secondarycolor: secondarycolor,
              };
              props.setSiteConfig(newSiteConfig);
              props.siteConfigRef.current = newSiteConfig;
            }
            props.setOrgName(values.name);
            navigate("/app");
          } else if (res.errors) {
            lsEnqueueSnackbar(res.errors[0].message, {
              variant: "error",
            });
            setSubmitting(false);
          }
        })
        .catch((e) => {
          lsEnqueueSnackbar(e.message, {
            variant: "error",
          });
          setSubmitting(false);
        }); // catch network errors
    },
  });

  let pageResults;
  if (getOPLoading) {
    pageResults = <Waiting />;
  } else if (getOPData) {
    if (!orgProfile.current) {
      orgProfile.current = getOPData.orgProfile;
    }
    pageResults = (
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} paddingLeft="20%" paddingRight="20%">
          <Grid item xs={12}>
            <Typography
              component="h1"
              variant="h5"
              sx={{ marginBottom: 3, fontWeight: "bold" }}
            >
              {t("OrgDetails")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <OrgLogoForm
              orgProfile={orgProfile}
              setOrgProfile={setOrgProfile}
              siteConfig={props.siteConfig}
              setSiteConfig={props.setSiteConfig}
              siteConfigRef={props.siteConfigRef}
              logo={props.logo}
              setLogo={props.setLogo}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={1}>
                <Avatar
                  variant="square"
                  style={{ backgroundColor: primarycolor }}
                >
                  <PaletteIcon />
                </Avatar>
              </Grid>
              <Grid item xs={11}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClickPrimaryColor}
                >
                  {t("SelectPrimaryColor")}
                </Button>
                {displayColorPrimaryPicker ? (
                  <div style={popover}>
                    <div style={cover} onClick={handleClosePrimaryColor} />
                    <SketchPicker
                      color={primarycolor}
                      onChangeComplete={handlePrimaryColorChange}
                    />
                  </div>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={1}>
                <Avatar
                  variant="square"
                  style={{ backgroundColor: secondarycolor }}
                >
                  <PaletteIcon />
                </Avatar>
              </Grid>
              <Grid item xs={11}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClickSecondaryColor}
                >
                  {t("SelectSecondaryColor")}
                </Button>
                {displayColorSecondaryPicker ? (
                  <div style={popover}>
                    <div style={cover} onClick={handleCloseSecondaryColor} />
                    <SketchPicker
                      color={secondarycolor}
                      onChangeComplete={handleSecondaryColorChange}
                    />
                  </div>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography component="p">&nbsp;</Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="name"
              label={t("OrgName")}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>

          {/* <Grid item xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              id="address"
              label={t("Address")}
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              fullWidth
              id="city"
              label={t("City")}
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              variant="outlined"
              sx={{
                margin: 0,
                width: "100%",
                display: "flex",
                flexWrap: "nowrap",
              }}
            >
              <InputLabel ref={inputLabel} id="state-label">
                {t("State")}
              </InputLabel>
              <Select
                label={t("State")}
                labelId="state-label"
                id="state"
                name="state"
                value={formik.values.state}
                onChange={formik.handleChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value={"California"}>California</MenuItem>
                <MenuItem value={"Ontario"}>Ontario</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              fullWidth
              id="postalcode"
              label={t("PostalCode")}
              name="postalcode"
              value={formik.values.postalcode}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              variant="outlined"
              sx={{
                margin: 0,
                width: "100%",
                display: "flex",
                flexWrap: "nowrap",
              }}
            >
              <InputLabel ref={inputLabel} id="country-label">
                {t("Country")}
              </InputLabel>
              <Select
                label={t("Country")}
                labelId="country-label"
                id="country"
                name="country"
                defaultValue=""
                value={formik.values.country}
                onChange={formik.handleChange}
              >
                <MenuItem value={"Canada"}>{t("Canada")}</MenuItem>
                <MenuItem value={"UnitedStates"}>{t("UnitedStates")}</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={12}>
            <FormControl
              variant="outlined"
              sx={{
                margin: 0,
                width: "100%",
                display: "flex",
                flexWrap: "nowrap",
              }}
            >
              <InputLabel ref={inputLabel} id="language-label">
                {t("PreferredLanguage")}
              </InputLabel>
              <Select
                label={t("PreferredLanguage")}
                labelId="language-label"
                id="language"
                name="language"
                defaultValue=""
                value={formik.values.language}
                onChange={formik.handleChange}
              >
                <MenuItem value={"English"}>{t("English")}</MenuItem>
                <MenuItem value={"French"}>{t("French")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid item xs={12}>
            <FormControl
              variant="outlined"
              sx={{
                margin: 0,
                width: "100%",
                display: "flex",
                flexWrap: "nowrap",
              }}
            >
              <InputLabel ref={inputLabel} id="timezone-label">
                {t("PreferredTimeZone")}
              </InputLabel>
              <Select
                label={t("PreferredTimeZone")}
                labelId="timezone-label"
                id="timezone"
                name="timezone"
                defaultValue=""
                value={formik.values.timezone}
                onChange={formik.handleChange}
              >
                <MenuItem value={"AST"}>AST</MenuItem>
                <MenuItem value={"EST"}>EST</MenuItem>
                <MenuItem value={"CST"}>CST</MenuItem>
                <MenuItem value={"MST"}>MST</MenuItem>
                <MenuItem value={"PST"}>PST</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}
          <Grid container justifyContent="flex-end">
            <Grid item sx={{ textAlign: "right" }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{ marginTop: 3, marginRight: 0, marginBottom: 1.5 }}
              >
                {t("Update")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  } else {
    pageResults = "";
  }
  return <div>{pageResults}</div>;
}
